// src/components/invite/InvitePreviewRoute.tsx

import React, { useEffect, useState } from 'react';
import VideoThemePlayer from '../player/VideoThemePlayer';
import { TemplateType } from '../../types/Template';

type PreviewMsg =
  | 'REQUEST_PREVIEW_DATA'
  | { type: 'PREVIEW_DATA'; data: any; template: TemplateType };

/* ─── Utility: 24‑hour ➜ 12‑hour ─────────────────────────────────── */
function to12Hour(time24?: string | null): string {
  if (!time24 || typeof time24 !== 'string') return '';

  // If the string already has AM/PM just normalise spacing and return
  if (/\b(am|pm)\b/i.test(time24)) {
    return time24.replace(/\s+/g, ' ').trim();
  }

  const m = time24.trim().match(/^(\d{1,2}):?(\d{2})?$/);
  if (!m) return time24;                  // Unrecognised → leave unchanged

  let   hrs  = parseInt(m[1], 10);
  const mins = parseInt(m[2] ?? '0', 10);

  if (hrs > 23 || mins > 59) return time24;   // Fail fast on nonsense

  const period = hrs >= 12 ? 'PM' : 'AM';
  hrs = hrs % 12 || 12;                       // 0→12, 13→1, etc.

  return `${hrs}:${mins.toString().padStart(2, '0')} ${period}`;
}

export default function InvitePreviewRoute() {
  const [data,     setData]     = useState<any | null>(null);
  const [template, setTemplate] = useState<TemplateType | null>(null);

  /* 1️⃣ ask the opener for data */
  useEffect(() => {
    window.opener?.postMessage('REQUEST_PREVIEW_DATA', window.location.origin);
  }, []);

  /* 2️⃣ receive payload or recover after refresh */
  useEffect(() => {
    const recv = (e: MessageEvent<PreviewMsg>) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && typeof e.data === 'object' && e.data.type === 'PREVIEW_DATA') {
        // Check if this is a real guest name or a generic placeholder
        const isRealGuestName = e.data.data.recipientName && 
                               e.data.data.recipientName !== 'Anonymous' &&
                               !e.data.data.recipientName.match(/^(HEY YOU!?|Greetings,?\s*(Sports\s*Fan|Friend)|Dear\s*Guest)$/i);
        
        // Process recipient name: use first name for real guests, empty string for placeholders
        const recipientFirstName = isRealGuestName
          ? e.data.data.recipientName.trim().split(' ')[0]
          : '';

        // Convert times to 12-hour format and process recipient name
        const processedData = {
          ...e.data.data,
          sTime: to12Hour(e.data.data.sTime),
          eTime: to12Hour(e.data.data.eTime),
          recipientName: recipientFirstName,
        };
        
        setData(processedData);
        setTemplate(e.data.template);
        sessionStorage.setItem('invitePreviewData',     JSON.stringify(processedData));
        sessionStorage.setItem('invitePreviewTemplate', JSON.stringify(e.data.template));
      }
    };
    window.addEventListener('message', recv);

    const cachedData     = sessionStorage.getItem('invitePreviewData');
    const cachedTemplate = sessionStorage.getItem('invitePreviewTemplate');
    if (cachedData && cachedTemplate) {
      const parsedData = JSON.parse(cachedData);
      
      // Check if this is a real guest name or a generic placeholder
      const isRealGuestName = parsedData.recipientName && 
                             parsedData.recipientName !== 'Anonymous' &&
                             !parsedData.recipientName.match(/^(HEY YOU!?|Greetings,?\s*(Sports\s*Fan|Friend)|Dear\s*Guest)$/i);
      
      // Process recipient name: use first name for real guests, empty string for placeholders
      const recipientFirstName = isRealGuestName
        ? parsedData.recipientName.trim().split(' ')[0]
        : '';

      // Apply time conversion and recipient name processing to cached data
      const processedCachedData = {
        ...parsedData,
        sTime: to12Hour(parsedData.sTime),
        eTime: to12Hour(parsedData.eTime),
        recipientName: recipientFirstName,
      };
      setData(processedCachedData);
      setTemplate(JSON.parse(cachedTemplate));
    }
    return () => window.removeEventListener('message', recv);
  }, []);

  /* 3️⃣ one-time CSS helper: centre the phone */
  useEffect(() => {
    const sheet = document.createElement('style');
    sheet.textContent = `
      /* centre the player's root */
      .video-theme-player.loaded {
        display:flex !important;
        justify-content:center !important;
      }
    `;
    document.head.appendChild(sheet);
    return () => sheet.remove();
  }, []);

  /* 4️⃣ loading guard */
  if (!data || !template) {
    return (
      <div style={{
        display:'flex',alignItems:'center',justifyContent:'center',
        width:'100vw',height:'100vh',background:'#000',color:'#fff',
        font:'600 1.2rem Montserrat, sans-serif'
      }}>
        Loading invitation…
      </div>
    );
  }

  /* 5️⃣ full-screen preview */
  return (
    <div style={{
      width:'100vw',
      height:'100vh',
      background:'#000',
      overflow:'hidden',
      display:'flex',
      alignItems:'center',
      justifyContent:'center'
    }}>
      <VideoThemePlayer template={template} data={data} mode="preview" />
    </div>
  );
}